import { PdsWidget } from './pds-widget';
import { sanitizeHtmlString } from './pds-xss';

/**
 * This complements PdsRichTextEditor.  The resulting html from this editor can be displayed using this class.
 * It takes care of sanitizing the html to rid of security issues and also modifies links in the html code to
 * ensure that a new tab common and the same tab (named 'portalExternalLink') will be used for any clicked links.
 * It also ensures that paragraph margins matches that of what CKEditor uses; not superceeded by PDS or custom styling
 * (due to CSS specificity).
 */
export class PdsRichTextDisplay extends PdsWidget {
  is = 'pds-rich-text-display';

  linkOpensNewTab: boolean;

  properties = {
    html: {
      type: String,
      observer: 'htmlChanged'
    },
    linkOpensNewTab: {
      type: String,
      value: 'true',
      observer: 'setLinkOpensNewTab'
    }
  };

  messageKeysUpdated(): void {
    //no op since there are no keys here.
  }

  htmlChanged(value: string): void {
    let el = <HTMLElement>this.querySelector('.pds-rich-text-display-content');
    if (el) {
      let html = '';
      if (value != null && value.length > 0) {
        html = sanitizeHtmlString(value);
        if (this.linkOpensNewTab) {
          html = this.updateLinks(html);
        }
      }
      el.innerHTML = html;
    }
  }

  setLinkOpensNewTab(value: any): void {
    this.linkOpensNewTab = (value === 'true' || value === true);
  }

  /**
   * This function modifies links in the provided html code to set the 'target' attribute to 'portalExternalLink' so
   * that any links embedded through the CKEditor will navigate to a new tab when clicked.  This is the same tab used
   * in UC when clicking external links.  This is a general requirement to address links replacing the UC page.
   *
   * @param html - the html code to scan for links.
   */
  updateLinks(html: string): string {
    let dom = document.createElement('div');
    dom.innerHTML = html; // Parse

    let links = dom.querySelectorAll('a');

    // Cannot use for (let link of links) because links is not an Array type.
    for (let i = 0; i < links.length; i++) {
      let link = links[i];
      if (link != null) {
        let target = link.getAttribute('target');

        // Tidbit
        // ------
        //  If target="_blank", rel="noreferrer noopener" should be added.  "noreferrer" works with newer browsers.
        //  References:
        //    https://github.com/asciidoctor/asciidoctor/issues/2071
        //    https://blog.templatetoaster.com/rel-noreferrer-noopener-seo/
        //
        //  Since we are replacing "_blank", we do not need to bother adding rel="noreferrer noopener"
        if (target == null || target === '_self' || target === '_parent' || target === '_top' || target === '_blank') {
          link.setAttribute('target', 'portalExternalLink');
        }
      }
    }
    return dom.innerHTML;
  }
}
